import type { ContextSourceType } from '@fos/shared/contracts/copilot';
import type { EmailDraftAiNotes } from '@fos/shared/contracts/mail';
import { aiProviderLabel, CONTEXT_SOURCE_TYPE_LABELS } from '@fos/shared/labels';
import { Sparkles } from 'lucide-react';
import { useId, useState } from 'react';
import { formatDateShort, formatDateTimeShort } from '../../../lib/format';
import { ContextBanner } from '../../../ui/Blocks';
import { Status } from '../../../ui/Status';

/**
 * Szkic z „Przygotuj odpowiedź z AI” w kompozytorze (DESIGN §2 zasada 5 „Propozycje AI mają źródła i skutki”, MVP §11):
 * „Szkic AI” z dostawcą i trwałym oznaczeniem symulacji, ostrzeżenia (np. polecenia w treści maila), pytania do
 * wyjaśnienia i jawne założenia, rozwijana lista źródeł. Widoczne także po ponownym otwarciu szkicu.
 */
export function AiDraftNotes({ notes }: { notes: EmailDraftAiNotes }) {
  const [open, setOpen] = useState(false);
  const listId = useId();
  return (
    <section aria-label="Szkic AI" className="flex flex-col gap-2 rounded-lg bg-surface px-3 py-2.5 text-caption">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-fg-muted">
        <Sparkles size={13} strokeWidth={1.75} aria-hidden className="shrink-0 text-accent" />
        <span className="font-medium text-fg-secondary">Szkic AI</span>
        {notes.simulated ? (
          <Status tone="warning">Symulacja</Status>
        ) : (
          <span>
            {aiProviderLabel(notes.provider)}
            {notes.model ? ` · ${notes.model}` : ''}
          </span>
        )}
        <span>{formatDateTimeShort(notes.generatedAt)}</span>
        {notes.sources.length > 0 && (
          <>
            <span aria-hidden>·</span>
            <button
              type="button"
              aria-expanded={open}
              aria-controls={listId}
              onClick={() => setOpen((v) => !v)}
              className="rounded-xs text-fg-secondary hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
            >
              {open ? 'Ukryj źródła' : `Źródła (${notes.sources.length})`}
            </button>
          </>
        )}
      </p>
      {notes.simulated && (
        <p className="text-fg-muted">Odpowiedź z adaptera testowego — nic nie zostało wysłane do dostawcy AI.</p>
      )}
      {open && notes.sources.length > 0 && (
        <ul id={listId} aria-label="Źródła" className="flex flex-col gap-1">
          {notes.sources.map((src) => (
            <li key={`${src.type}:${src.id ?? src.title}`} className="flex min-w-0 items-baseline gap-2">
              <span className="min-w-0 truncate text-fg-secondary">
                {CONTEXT_SOURCE_TYPE_LABELS[src.type as ContextSourceType] ?? src.type}:{' '}
                {src.title.replace(/^[^:]+:\s*/, '')}
              </span>
              {src.date && <span className="shrink-0 text-fg-muted">{formatDateShort(src.date)}</span>}
              {src.untrusted && <span className="shrink-0 text-fg-muted">· dane klienta</span>}
            </li>
          ))}
        </ul>
      )}
      {notes.warnings.map((w) => (
        <ContextBanner key={w} tone="warning">
          {w}
        </ContextBanner>
      ))}
      {notes.questions.length > 0 && (
        <div>
          <p className="font-medium text-fg-secondary">Do wyjaśnienia</p>
          <ul className="mt-0.5 list-disc pl-5 text-fg-secondary">
            {notes.questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        </div>
      )}
      {notes.assumptions.length > 0 && (
        <div>
          <p className="font-medium text-fg-secondary">Założenia</p>
          <ul className="mt-0.5 list-disc pl-5 text-fg-secondary">
            {notes.assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
